import { Button, Card, Divider, Flex, Radio } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

// components
import BackButton from 'components/backButton/backButton';
import PageTitle from 'components/title/title';
import { onAddVote, onGetDecisions } from '../services/assembliesServices';
import { Decision } from '../models/assemblies';
import NotificationService from 'core/services/notification';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import { isRTL } from 'core/services/language';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import { AddVotePayload, Vote } from '../models/attendance';

// services

// styles

// models

interface ActionAssembliesVotePageProps {}

const ActionAssembliesVotePage: FunctionComponent<ActionAssembliesVotePageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { assemblyId, shareholderId } = useParams();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [decisions, setDecisions] = useState<Decision[]>([]);

	const [votes, setVotes] = useState<Map<string, string>>(new Map());

	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);

	const assemblyTypeOptions = [
		{
			value: 'Yes',
			label: <p className="mb-0">{t('ACTIONS.YES')}</p>,
		},
		{
			value: 'No',
			label: <p className="mb-0">{t('ACTIONS.NO')}</p>,
		},
		{
			value: 'Abstained',
			label: <p className="mb-0">{t('ACTIONS.ABSTAINED')}</p>,
		},
	];

	useEffect(() => {
		getDecisions();
	}, []);

	const getDecisions = async () => {
		try {
			setIsLoading(true);

			const response = await onGetDecisions(String(assemblyId), String(shareholderId));
			setDecisions(response);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.FETCH_DECISION_ERROR'));
			navigate(`/dashboard/actions/assemblies/${assemblyId}/attendance`);
		}
	};

	const addVoteHandler = async () => {
		try {
			setIsSubmitting(true);

			const payload = new FormData();
			payload.append('attendance_id', String(shareholderId));
			votes.forEach((value, key) => payload.append(`responses[${key}]`, value as Vote));

			await onAddVote(assemblyId as string, payload);

			setIsSubmitting(false);
			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.VOTE_ADDED_SUCCESSFULLY'));
			navigate(`/dashboard/actions/assemblies/${assemblyId}/attendance`);
		} catch (error: any) {
			setIsSubmitting(false);
			const message = error?.response?.data?.message || t('COMMON.ERROR');
			NotificationService.error(t('COMMON.ERROR'), message);
		}
	};

	const handleVoteChange = (event: any, decisionId: number) => {
		const { value } = event.target;
		setVotes((prevVotes) => {
			const updatedVotes = new Map(prevVotes);
			updatedVotes.set(decisionId.toString(), value);
			return updatedVotes;
		});
	};

	return (
		<>
			<PageTitle name={t('PAGES.ASSEMBLY_ATTENDANCE')} />

			<BackButton />

			{isLoading ? (
				<LoadingComponent />
			) : (
				<Card>
					<h5>{t('ACTIONS.ADD_VOTE')}</h5>

					{decisions.length > 0 &&
						decisions.map((decision, index) => (
							<div key={index}>
								<div>
									<Flex align="center" gap={15} className="mt-4">
										<h6 className="mb1">
											{index + 1} - {isRTL() ? decision.translated_title.ar : decision.translated_title.en ?? decision.translated_title.ar}
										</h6>

										{decision.is_abstained && <span style={{ color: 'red' }}>( {t('ACTIONS.ABSTENTION_FROM_VOTING')} )</span>}
									</Flex>
									<Radio.Group
										onChange={(event) => handleVoteChange(event, decision.id)}
										className="customRadio"
										style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
										options={assemblyTypeOptions}
										disabled={decision.is_abstained}
										defaultValue={decision.is_abstained ? 'Abstained' : null}
									/>
								</div>

								{index < decisions.length - 1 && <Divider />}
							</div>
						))}

					<Flex justify="flex-end">
						<Button
							type="primary"
							className="mt-4"
							onClick={() => {
								setConfirmModalOpen(true);
							}}>
							{t('COMMON.ADD')}
						</Button>
					</Flex>
				</Card>
			)}

			<ConfirmModalComponent
				open={confirmModalOpen}
				confirmText={t('COMMON.CONFIRM')}
				onConfirm={addVoteHandler}
				onDiscard={() => setConfirmModalOpen(false)}
				title={t('ACTIONS.SUBMIT_SHAREHOLDER_VOTE')}
				noteType='red'
				note={t('ACTIONS.CONFIRM_SUBMIT_VOTE_NOTE')}
				isLoading={isSubmitting}
			/>
		</>
	);
};

export default ActionAssembliesVotePage;
